import { Filter } from '@weave/schema-gen-ts/dist/schemas/call-intelligence/v2';
import { TaskType } from '@weave/schema-gen-ts/dist/schemas/task-center/shared/v1/enums.pb';
import { AIOutcome, AppointmentType } from '@weave/schema-gen-ts/dist/shared/call-intelligence/enums.pb';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { validateUUIDV4 } from '@frontend/string';
import { CALL_INTEL_CALL_STATUSES, CallIntelTimePeriodLabelToType, timePeriodLabels } from '../constant';
import {
  type ApiPayloadProps,
  type CallEditableFields,
  type CallIntelSortOption,
  type CallIntelSubView,
  type CallRecord,
  type CallTaskListRequest,
  type CallTaskParams,
  type ChangeType,
  type CommonFilterPayloadParams,
  type DateRange,
  type DrillDownOptions,
  type Filters,
  type FollowupTaskTypeFilters,
  type GetServiceQualityStatsIO,
  type ListCallBaseRequestParams,
  type ListCallsIO,
  type ListCallsRequestPayload,
  type ListFollowupsIO,
  type PhoneNumber,
  type TransformEditableFieldType,
  CallDirectionEnum,
  CallStatusEnum,
  CategoryEnum,
  ContactTypeEnum,
  FilterTypeEnum,
  FollowupFilterTypeIndexEnum,
  FollowupSortByIndexEnum,
  IncludesHydration,
  SentimentEnum,
  SearchTypeEnum,
  ServiceQualityFlagEnum,
  SortType,
  SortTypeIndexEnum,
  TaskFilterTypeEnum,
  TaskSourceEnum,
  TaskSortTypeEnum,
  TimePeriodTypeEnum,
} from '../types';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isBetween);
/**
 * To get the drill down filter keys for overview page
 */
export const getDrillDownFilterKeys = ({ id, type }: CallIntelSubView): DrillDownOptions | undefined => {
  if (!id || !type) return undefined;

  switch (type) {
    case 'appointment-type':
      return {
        index: FilterTypeEnum.FILTER_BY_APPOINTMENT_TYPE,
        key: 'appointment_types',
        value: [id],
      };

    case 'category':
      return {
        index: FilterTypeEnum.FILTER_BY_CATEGORY,
        key: 'categories',
        value: [id],
      };

    case 'sentiment':
      return {
        index: FilterTypeEnum.FILTER_BY_SENTIMENT,
        key: 'sentiments',
        value: [id],
      };

    case 'service-quality':
      return {
        index: FilterTypeEnum.FILTER_BY_SERVICE_QUALITY,
        key: 'service_quality_flags',
        value: [id],
      };

    default:
      return undefined;
  }
};

export const getPhoneNumber = (phoneNumber?: PhoneNumber): string => {
  if (!phoneNumber) return '';

  const { countryCode, nationalNumber } = phoneNumber;
  return `${countryCode}${nationalNumber}`;
};

export const transformEditableFields = (
  fields: Partial<CallEditableFields>,
  type: TransformEditableFieldType = 'edit'
): Record<string, any> => {
  const transformed: Record<string, any> = {};

  const keyMap: Partial<Record<keyof CallEditableFields, string>> = {
    contactType: 'contact_type',
    schedulingOutcome: 'scheduling_outcome',
    schedulingOpportunity: 'scheduling_opportunity',
    categories: 'categories',
    schedulingOutcomeV2: 'scheduling_outcome_v2',
  };

  // direct mappings
  for (const [key, transformedKey] of Object.entries(keyMap) as [keyof CallEditableFields, string][]) {
    if (Object.hasOwn(fields, key)) {
      transformed[transformedKey] = fields[key];
    }
  }

  if (Object.hasOwn(fields, 'callSentiment')) {
    if (fields.callSentiment === null) {
      transformed.call_sentiment = null;
    } else {
      transformed.call_sentiment =
        type === 'edit'
          ? { sentiment: fields.callSentiment }
          : { sentiment: fields.callSentiment?.sentiment ?? fields.callSentiment };
    }
  }

  if (Object.hasOwn(fields, 'appointmentTypes')) {
    if (fields.appointmentTypes === null) {
      transformed.appointment_types = { appointment_type: [] };
    } else {
      transformed.appointment_types =
        type === 'edit'
          ? { appointment_type: fields.appointmentTypes }
          : { appointment_type: fields.appointmentTypes?.appointmentType };
    }
  }

  if (fields.serviceQualityFlags) {
    transformed.service_quality_flags = fields.serviceQualityFlags.map((flag) => ({
      service_quality_flag: type === 'edit' ? flag : flag.serviceQualityFlag,
      is_disabled: false,
    }));
  }

  return transformed;
};

export const generateListTasksPayload = (callTaskParams: CallTaskParams): CallTaskListRequest => {
  const payload: CallTaskListRequest = {
    filter: {
      [TaskFilterTypeEnum.FILTER_BY_LOCATION]: {
        locationId: callTaskParams.locations || [],
      },
      [TaskFilterTypeEnum.FILTER_BY_SOURCE]: {
        source: callTaskParams.source || TaskSourceEnum.SOURCE_CALL_INTELLIGENCE,
      },
      [TaskFilterTypeEnum.FILTER_BY_SOURCE_ID]: {
        sourceId: callTaskParams.callIds,
      },
    },
    includeMetadata: callTaskParams.includeMetadata ?? false,
    sortType: callTaskParams.sortType ?? TaskSortTypeEnum.SORT_DESC,
    isDemo: callTaskParams.isDemo ?? false,
    demoMetadata: callTaskParams.demoMetadata ?? undefined,
  };
  if (payload.filter && callTaskParams?.taskStatusFilters?.length) {
    payload.filter[TaskFilterTypeEnum.FILTER_BY_STATUS] = {
      status: callTaskParams.taskStatusFilters,
    };
  }

  if (payload.filter && callTaskParams?.taskTypes?.length) {
    payload.filter[TaskFilterTypeEnum.FILTER_BY_TYPE] = {
      type: callTaskParams.taskTypes,
    };
  }

  return payload;
};

/**
 * Given a Call Intel time period label (e.g., "Today", "This Week"),
 * returns the corresponding TimePeriodType.
 * If no direct mapping exists, returns TIMEPERIOD_TYPE_CUSTOM.
 */
export const convertTimePeriodLabelToEnum = (timePeriodLabel?: string): TimePeriodTypeEnum => {
  return CallIntelTimePeriodLabelToType[timePeriodLabel ?? ''] ?? TimePeriodTypeEnum.TIMEPERIOD_TYPE_CUSTOM;
};

export const formatDateRangeForPayload = ({
  startDate,
  endDate,
  timePeriodLabel,
  timePeriod,
  timezone,
}: {
  startDate?: string;
  endDate?: string;
  timePeriodLabel?: string;
  timePeriod?: TimePeriodTypeEnum;
  timezone?: string;
}): DateRange => {
  // Get the current timezone offset in minutes
  const timezoneOffsetMinutes = dayjs().utcOffset();
  const start = dayjs(startDate).startOf('day').add(-timezoneOffsetMinutes, 'minute').format('YYYY-MM-DD HH:mm:ss.SSS');
  const end = dayjs(endDate).endOf('day').add(-timezoneOffsetMinutes, 'minute').format('YYYY-MM-DD HH:mm:ss.SSS');

  return {
    start,
    end,
    timePeriod: timePeriod ?? convertTimePeriodLabelToEnum(timePeriodLabel),
    timezone: timezone ?? dayjs.tz.guess(),
  };
};

export const generateCommonFilterPayload = ({
  filters,
  dateRangeParams,
}: CommonFilterPayloadParams): Required<Pick<ListCallsIO['input'], 'filter'>> => {
  const { start, end } = formatDateRangeForPayload(filters);

  return {
    filter: {
      [FilterTypeEnum.FILTER_BY_DATE_RANGE]: {
        dateRange: {
          end,
          start,
          ...(dateRangeParams?.timePeriod && { time_period: dateRangeParams.timePeriod, timezone: dayjs.tz.guess() }),
        },
      },
      [FilterTypeEnum.FILTER_BY_LOCATION]: {
        locations: filters.locations,
      },
    },
  };
};

export const generateCallsFilterPayload = ({ filters }: { filters: Filters }): ListCallsIO['input'] => {
  const {
    aiOutcomes,
    aiEvents,
    appointmentTypes,
    callDirection,
    categories,
    contactTypes,
    officeNumbers,
    officeUsers,
    officeHours,
    schedulingOutcomeV2s,
    sentiments,
    serviceQualityFlags,
    sipId,
  } = filters;

  const payload = generateCommonFilterPayload({ filters });

  payload.filter[FilterTypeEnum.FILTER_BY_CALL_STATUS] = {
    callStatus: filters.hideNonAnalyzedCalls
      ? // As we need to hide calls which are not analyzed, we need to exclude failed and skipped calls
        CALL_INTEL_CALL_STATUSES.filter(
          (status) =>
            status !== CallStatusEnum.CALL_STATUS_FAILED &&
            status !== CallStatusEnum.CALL_STATUS_ERROR &&
            status !== CallStatusEnum.CALL_STATUS_SKIPPED
        )
      : [...CALL_INTEL_CALL_STATUSES],
  };

  if (aiEvents?.length) {
    payload.filter[FilterTypeEnum.FILTER_BY_AI_RECEPTIONIST_EVENT_TYPE] = {
      aiReceptionistEventTypes: aiEvents,
    };
  } else if (aiOutcomes?.length) {
    payload.filter[FilterTypeEnum.FILTER_BY_AI_OUTCOME] = {
      aiOutcome: aiOutcomes as AIOutcome[],
    };
  }

  if (appointmentTypes?.length) {
    payload.filter[FilterTypeEnum.FILTER_BY_APPOINTMENT_TYPE] = {
      appointmentTypes: appointmentTypes as AppointmentType[],
    };
  }

  if (callDirection) {
    payload.filter[FilterTypeEnum.FILTER_BY_CALL_DIRECTION] = {
      callDirection: [callDirection] as CallDirectionEnum[],
    };
  }

  if (categories?.length) {
    payload.filter[FilterTypeEnum.FILTER_BY_CATEGORY] = {
      categories: categories as CategoryEnum[],
    };
  }

  if (contactTypes?.length) {
    payload.filter[FilterTypeEnum.FILTER_BY_CONTACT_TYPE] = {
      contactType: contactTypes as ContactTypeEnum[],
    };
  }

  if (sentiments?.length) {
    payload.filter[FilterTypeEnum.FILTER_BY_SENTIMENT] = {
      sentiments: sentiments as SentimentEnum[],
    };
  }

  if (schedulingOutcomeV2s?.length) {
    payload.filter[FilterTypeEnum.FILTER_BY_SCHEDULING_OUTCOME_V2] = {
      schedulingOutcomeV2: schedulingOutcomeV2s,
    };
  }

  if (officeNumbers?.length) {
    payload.filter[FilterTypeEnum.FILTER_BY_OFFICE_NUMBER] = {
      officeNumber: officeNumbers,
    };
  }

  if (officeUsers?.length) {
    payload.filter[FilterTypeEnum.FILTER_BY_OFFICE_USER] = {
      officeUserId: officeUsers,
    };
  }

  if (serviceQualityFlags?.length) {
    payload.filter[FilterTypeEnum.FILTER_BY_SERVICE_QUALITY] = {
      serviceQualityFlags: serviceQualityFlags as ServiceQualityFlagEnum[],
    };
  }

  if (officeHours?.length) {
    payload.filter[FilterTypeEnum.FILTER_BY_OFFICE_HOURS] = {
      officeHours,
    };
  }

  if (sipId?.length) {
    payload.filter[FilterTypeEnum.FILTER_BY_SIP_ID] = {
      sipId,
    };
  }

  return payload;
};

export const generateDrillDownPayload = (drillDownOptions: DrillDownOptions) => {
  const { index, key, value } = drillDownOptions;

  return {
    [index]: {
      [key]: value,
    },
  };
};

type ChartsPayload = { filter: Record<string, Filter> } & Omit<ApiPayloadProps, 'filters'>;

export const generateChartsPayload = ({ drillDownOptions, filters }: ApiPayloadProps): ChartsPayload => {
  const payload = generateCommonFilterPayload({ filters });

  if (filters.contactTypes?.length) {
    payload.filter[FilterTypeEnum.FILTER_BY_CONTACT_TYPE] = {
      contactType: filters.contactTypes as ContactTypeEnum[],
    };
  }

  if (filters.officeUsers?.length) {
    payload.filter[FilterTypeEnum.FILTER_BY_OFFICE_USER] = {
      officeUserId: filters.officeUsers,
    };
  }

  if (filters.officeHours?.length) {
    payload.filter[FilterTypeEnum.FILTER_BY_OFFICE_HOURS] = {
      officeHours: filters.officeHours,
    };
  }

  payload.filter = {
    ...payload.filter,
    [FilterTypeEnum.FILTER_BY_CALL_STATUS]: {
      callStatus: [CallStatusEnum.CALL_STATUS_COMPLETE],
    },
  };

  if (drillDownOptions) {
    payload.filter = {
      ...payload.filter,
      ...generateDrillDownPayload(drillDownOptions),
    };
  }

  return payload;
};

export const generateSearchPayload = (searchQuery: string) => {
  let searchType: SearchTypeEnum = SearchTypeEnum.SEARCH_BY_NAME;

  if (/^\d+$/.test(searchQuery)) {
    searchType = SearchTypeEnum.SEARCH_BY_NUMBER;
  }

  return {
    search_type: searchType,
    search: searchQuery,
  };
};

export const generateBaseCallsRequest = ({
  drillDownOptions,
  filters,
  searchQuery,
  sortType,
  includesAnalysis,
  callVisibility,
}: ListCallBaseRequestParams): ListCallsIO['input'] => {
  const search = searchQuery?.search ?? '';
  const searchType = searchQuery?.searchType;
  const isUUIDSearch = searchQuery ? validateUUIDV4(search) : false;

  const basePayload = generateCallsFilterPayload({ filters });

  const filter = {
    ...basePayload.filter,
    ...(drillDownOptions ? generateDrillDownPayload(drillDownOptions) : {}),
  };

  if (isUUIDSearch && searchQuery) {
    filter[FilterTypeEnum.FILTER_BY_CALL_ID] = {
      callId: [search],
    };
  }

  const payload = {
    filter,
    sortType: sortType ?? SortType.SORT_DESC,
    ...(search &&
      searchType &&
      !isUUIDSearch && {
        searchQuery,
      }),
    includesHydration: [
      IncludesHydration.HYDRATE_OFFICE_USER,
      IncludesHydration.HYDRATE_PERSON,
      IncludesHydration.HYDRATE_DEVICE_NAME,
    ],
    includesAnalysis,
    callVisibility,
  };

  return payload;
};

export const generateListCallsPayload = ({ limit, ...baseParams }: ListCallsRequestPayload): ListCallsIO['input'] => {
  const payload = generateBaseCallsRequest(baseParams);
  return {
    ...payload,
    limit,
  };
};

export const getFilteredEnumValues = <T extends Record<string, string>>(enumObj: T, excludeValue: T[keyof T]) =>
  Object.values(enumObj).filter((value) => value !== excludeValue);

export const fillTaskTypes = (
  partialTaskTypes: Partial<Record<TaskType, number>>,
  order: Exclude<TaskType, TaskType.TYPE_UNKNOWN>[]
): Record<Exclude<TaskType, TaskType.TYPE_UNKNOWN>, number> => {
  const filledTaskTypes = order.map((key) => [key, partialTaskTypes[key] ?? 0]);

  return Object.fromEntries(filledTaskTypes);
};

export const generateContactId = (call: CallRecord) => {
  if (call.person?.id) {
    return call.person.id;
  } else if (call.phoneNumber?.nationalNumber) {
    return getPhoneNumber(call.phoneNumber);
  }
  return `unknown-contact-${call.id}`;
};

export const getChangeType = (rateChange: number): ChangeType => {
  if (isNaN(rateChange)) return 'noData';
  if (rateChange > 0) return 'increased';
  if (rateChange < 0) return 'decreased';
  return 'unchanged';
};

export const isOngoingLabel = (label: string): boolean => {
  const today = dayjs();

  switch (label) {
    case timePeriodLabels.today:
    case timePeriodLabels['this-week']:
    case timePeriodLabels['this-month']:
    case timePeriodLabels['this-quarter']:
      return true;

    // If it's a custom range, check if it includes today.
    default:
      if (label.includes('-')) {
        const [startLabel, endLabel] = label.split(' - ').map((date) => dayjs(date.trim()));
        return today.isBetween(startLabel, endLabel, 'day', '[]');
      }

      // For all other cases (e.g., "Last Week", "Last Month"), return false
      return false;
  }
};

export const getServiceQualityStatsPayload = ({
  filters,
  dateRangeParams,
}: CommonFilterPayloadParams): GetServiceQualityStatsIO['input'] => {
  const payload = generateCommonFilterPayload({ filters, dateRangeParams });

  payload.filter = {
    ...payload.filter,
    [FilterTypeEnum.FILTER_BY_CALL_STATUS]: {
      callStatus: [CallStatusEnum.CALL_STATUS_COMPLETE],
    },
    [FilterTypeEnum.FILTER_BY_SERVICE_QUALITY]: {
      serviceQualityFlags: filters.serviceQualityFlags as ServiceQualityFlagEnum[],
    },
  };

  if (filters.officeHours?.length) {
    payload.filter[FilterTypeEnum.FILTER_BY_OFFICE_HOURS] = {
      officeHours: filters.officeHours,
    };
  }

  return {
    ...payload,
    includeComparison: true,
  } as GetServiceQualityStatsIO['input'];
};

export const generateFollowupsFilterPayload = (filters: FollowupTaskTypeFilters): ListFollowupsIO['input'] => {
  const { officeUsers, officeHours, contactTypes, taskTypes, taskStatus } = filters;

  const { start, end } = formatDateRangeForPayload(filters);

  const filter: ListFollowupsIO['input']['filter'] = {
    [FilterTypeEnum.FILTER_BY_DATE_RANGE]: {
      dateRange: {
        end,
        start,
      },
    },
    [FilterTypeEnum.FILTER_BY_LOCATION]: {
      locations: filters.locations,
    },
  };

  if (officeUsers?.length) {
    filter[FollowupFilterTypeIndexEnum.FOLLOWUPS_FILTER_BY_OFFICE_USER] = {
      officeUserId: officeUsers,
    };
  }
  if (contactTypes?.length) {
    filter[FollowupFilterTypeIndexEnum.FOLLOWUPS_FILTER_BY_CONTACT_TYPE] = {
      contactType: contactTypes as ContactTypeEnum[],
    };
  }
  if (taskTypes?.length) {
    filter[FollowupFilterTypeIndexEnum.FOLLOWUPS_FILTER_BY_TASK_TYPE] = {
      taskType: taskTypes,
    };
  }
  if (taskStatus?.length) {
    filter[FollowupFilterTypeIndexEnum.FOLLOWUPS_FILTER_BY_TASK_STATUS] = {
      taskStatus: taskStatus,
    };
  }

  if (officeHours?.length) {
    filter[FollowupFilterTypeIndexEnum.FOLLOWUPS_FILTER_BY_OFFICE_HOURS] = {
      officeHours,
    };
  }

  return {
    filter,
  };
};

export const getFollowupSortValueByOption = (
  sortOption: CallIntelSortOption
): { sortBy: FollowupSortByIndexEnum; sortType: SortTypeIndexEnum } => {
  switch (sortOption) {
    case 'callCountHL':
      return {
        sortBy: FollowupSortByIndexEnum.FOLLOWUPS_SORT_CALLS_COUNT,
        sortType: SortTypeIndexEnum.SORT_DESC,
      };
    case 'callCountLH':
      return {
        sortBy: FollowupSortByIndexEnum.FOLLOWUPS_SORT_CALLS_COUNT,
        sortType: SortTypeIndexEnum.SORT_ASC,
      };
    case 'lastCallLatest':
      return {
        sortBy: FollowupSortByIndexEnum.FOLLOWUPS_SORT_LAST_CALL,
        sortType: SortTypeIndexEnum.SORT_DESC,
      };

    case 'lastCallOldest':
      return {
        sortBy: FollowupSortByIndexEnum.FOLLOWUPS_SORT_LAST_CALL,
        sortType: SortTypeIndexEnum.SORT_ASC,
      };
    default:
      return {
        sortBy: FollowupSortByIndexEnum.FOLLOWUPS_SORT_LAST_CALL,
        sortType: SortTypeIndexEnum.SORT_DESC,
      };
  }
};
